import { useEffect, useState } from "react";
import Pagination from "../../components/common/pagination/Pagination";
import Title from "../../components/common/title/Title";
import Button from "../../components/common/button/Button";
import { getApi, putApi, deleteApi } from "../../services/api/globalApi";
import { useNavigate } from "react-router";
import { PublicFileUrl } from "../../services/config";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { RxDragHandleDots2 } from "react-icons/rx";
import {
  successMessage,
  errorMessage,
} from "../../utils/toastify-message/ToastifyMessage";

function SortableTeamMemberItem({ item, index, onEdit, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <tr
      ref={setNodeRef}
      style={style}
      className="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
    >
      <td className="px-2 sm:px-4 py-3">
        <button
          {...attributes}
          {...listeners}
          type="button"
          className="cursor-grab text-gray-500 hover:text-gray-700"
        >
          <RxDragHandleDots2 size={18} />
        </button>
      </td>
      <td className="px-2 sm:px-4 py-3">
        <img
          src={`${PublicFileUrl}public/files/${item.image}`}
          alt={item.name}
          className="w-12 h-12 rounded-full object-cover"
        />
      </td>
      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm font-bold">
        {item.name}
      </td>
      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">{item.role}</td>
      <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm">
        {item.shortTagline}
      </td>
      <td className="px-2 sm:px-4 py-3 text-center">
        <span
          className={`px-2 py-1 rounded text-xs ${
            item.isActive
              ? "bg-green-100 text-green-800"
              : "bg-red-100 text-red-800"
          }`}
        >
          {item.isActive ? "فعال" : "غیرفعال"}
        </span>
      </td>
      <td className="px-2 sm:px-4 py-3 text-center">{index + 1}</td>
      <td className="px-2 sm:px-4 py-3">
        <div className="flex gap-1 sm:gap-2">
          <button
            onClick={() => onEdit(item)}
            className="text-xs sm:text-sm font-medium text-blue-600 dark:text-blue-500 hover:underline"
          >
            ویرایش
          </button>
          <button
            onClick={() => onDelete(item)}
            className="text-xs sm:text-sm font-medium text-red-600 dark:text-red-500 hover:underline"
          >
            حذف
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function TeamMemberList() {
  const [teamData, setTeamData] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const sensors = useSensors(useSensor(PointerSensor));

  const getTeamMembers = async (params) => {
    const path = "admin/team-members";
    try {
      const response = await getApi({ path, params: { ...params } });
      const dataWithIds = response?.data?.result.data.map((item) => ({
        ...item,
        id: item._id,
      }));
      setTeamData(dataWithIds);
      setPagination(response?.data?.result);
      setHasChanges(false);
    } catch (error) {
      console.log(error);
      errorMessage("خطا در دریافت اعضای تیم");
    }
  };

  useEffect(() => {
    getTeamMembers({});
  }, []);

  const [filter, setFilter] = useState();

  const filterDataHandler = (newParams) => {
    setFilter({ ...filter, ...newParams });
    const allFilter = {
      ...filter,
      ...newParams,
    };
    getTeamMembers(allFilter);
  };

  const navigate = useNavigate();

  const editTeamMember = (member) => {
    navigate("/edit-team-member", { state: member });
  };

  const addTeamMember = () => {
    navigate("/add-team-member");
  };

  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over?.id) {
      const oldIndex = teamData.findIndex((item) => item.id === active.id);
      const newIndex = teamData.findIndex((item) => item.id === over?.id);
      setTeamData((items) => arrayMove(items, oldIndex, newIndex));
      setHasChanges(true);
    }
  };

  const savePriorities = async () => {
    try {
      const items = teamData.map((item, index) => ({
        id: item._id,
        priority: index + 1,
      }));

      const path = "admin/team-members/update-priorities";
      await putApi({ path, body: { items } });
      successMessage("اولویت‌ها با موفقیت ذخیره شد");
      setHasChanges(false);
      getTeamMembers({});
    } catch (error) {
      console.log(error);
      errorMessage("خطا در ذخیره اولویت‌ها");
    }
  };

  const deleteTeamMember = async (member) => {
    if (window.confirm(`آیا از حذف "${member.name}" اطمینان دارید؟`)) {
      try {
        const path = `admin/team-members/${member._id}`;
        await deleteApi({ path });
        successMessage("عضو تیم با موفقیت حذف شد");
        getTeamMembers({});
      } catch (error) {
        console.log(error);
        errorMessage("خطا در حذف عضو تیم");
      }
    }
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-3xl p-6">
      <div className="flex justify-between items-center mb-6 gap-4">
        <Title title={"لیست اعضای تیم"} />
        <div className="flex gap-2">
          {hasChanges && (
            <Button
              onClick={savePriorities}
              text="ذخیره اولویت‌ها"
              color="primary"
              type="button"
              className="whitespace-nowrap min-w-fit"
            />
          )}
          <Button
            onClick={addTeamMember}
            text="افزودن عضو"
            color="success"
            type="button"
            className="whitespace-nowrap min-w-fit"
          />
        </div>
      </div>

      <Pagination
        pagination={pagination}
        filterDataHandler={filterDataHandler}
      />

      <div className="relative overflow-x-auto shadow-md sm:rounded-lg mt-6">
        {teamData?.length ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <table className="w-full text-xs sm:text-sm text-left rtl:text-right text-gray-500 dark:text-gray-400">
              <thead className="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
                <tr>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3 w-8"></th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    تصویر
                  </th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    نام
                  </th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    سمت
                  </th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    شعار
                  </th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3 text-center">
                    وضعیت
                  </th>
                  <th
                    scope="col"
                    className="px-2 sm:px-4 py-2 sm:py-3 text-center w-16"
                  >
                    اولویت
                  </th>
                  <th scope="col" className="px-2 sm:px-4 py-2 sm:py-3">
                    عملیات
                  </th>
                </tr>
              </thead>
              <SortableContext
                items={teamData.map((item) => item.id)}
                strategy={verticalListSortingStrategy}
              >
                <tbody>
                  {teamData?.map((item, index) => (
                    <SortableTeamMemberItem
                      key={item.id}
                      item={item}
                      index={index}
                      onEdit={editTeamMember}
                      onDelete={deleteTeamMember}
                    />
                  ))}
                </tbody>
              </SortableContext>
            </table>
          </DndContext>
        ) : (
          <div className="w-full flex items-center justify-center my-3 py-2 dark:text-white">
            <p>هیچ عضو تیمی هنوز اضافه نشده است.</p>
          </div>
        )}
      </div>

      <Pagination
        pagination={pagination}
        filterDataHandler={filterDataHandler}
      />
    </div>
  );
}
